import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { BasicUserWithPermissionRolesRes } from '@dealstack/shared/lib/generated/models/BasicUserWithPermissionRolesRes';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';

interface Options
	extends Omit<
		UseQueryOptions<
			BasicUserWithPermissionRolesRes[],
			unknown,
			BasicUserWithPermissionRolesRes[],
			[string, string | undefined]
		>,
		'queryKey' | 'queryFn' | 'staleTime'
	> {
	organizationId?: string;
}

const MINUTE = 60 * 1000;

const getOrganizationUsers = async (organizationId: string) => {
	const result = await makeRequest<ApiV1.Response<BasicUserWithPermissionRolesRes[]>>({
		endpoint: formatUnicorn(API_PATHS.ORGANIZATION_ORGANIZATIONID_USERS_GET, {
			organizationId,
		}),
		method: HTTP_METHOD.GET,
	});
	return result.payload ?? [];
};

export const useOrganizationUsers = (options: Options) => {
	const { organizationId, ...queryOptions } = options;
	return useQuery({
		queryKey: ['getOrganizationUsers', organizationId],
		staleTime: 2 * MINUTE,
		queryFn: () => {
			if (!organizationId) {
				return Promise.resolve([]);
			}
			return getOrganizationUsers(organizationId);
		},
		...queryOptions,
	});
};
